// Real-time typing indicator.  Listens for `note.typing` / `comm.typing`
// WebSocket events (see api/src/routes/realtimePresence.js) and renders a
// small "Alice is typing…" text banner scoped to the current matter.
//
// Filters:
//   - Ignores events on other matters
//   - Ignores self-echoes (the current user's own keystrokes)
//   - Auto-clears an entry after 5 seconds of silence from that user
//
// This is a compliance-neutral UX polish — no DB writes, no persistence,
// just live nudges over the existing WS bus.

import { useEffect, useState, useRef } from 'react'
import { useAuth } from '../context/AuthContext'
import api from '../services/api'

// Fire-and-forget notify.  Throttled — call as often as onChange fires, but
// we cap outbound requests to one per second.  The receiving side manages
// its own 5-second auto-clear so we don't need to send "stopped typing".
const lastSentAt = new Map()  // key: kind → timestamp
export function notifyTyping (kind, matterId) {
  if (!matterId) return
  const key = `${kind}:${matterId}`
  const now = Date.now()
  const last = lastSentAt.get(key) || 0
  if (now - last < 1500) return  // throttle
  lastSentAt.set(key, now)
  // Fire-and-forget: don't await + don't complain if it 429s
  api.post('/api/realtime/presence', {
    kind,
    matter_id: parseInt(matterId),
    label: kind === 'note.typing' ? 'Notes' : 'Communications',
  }).catch(() => {})
}

export default function TypingIndicator ({ matterId, kind }) {
  const { user } = useAuth()
  const [activeTypers, setActiveTypers] = useState({})  // { user_id: {name, at} }
  const timersRef = useRef({})  // { user_id: timeoutId }

  useEffect(() => {
    if (!matterId) return
    const handler = (ev) => {
      const msg = ev.detail
      if (!msg || msg.type !== kind) return
      const p = msg.payload || {}
      // Filter: only for this matter, and never self-echo
      if (String(p.matter_id) !== String(matterId)) return
      if (user && String(p.by_user_id) === String(user.id)) return
      // Add / update typer
      setActiveTypers(prev => ({
        ...prev,
        [p.by_user_id]: { name: p.by, at: p.at },
      }))
      // Auto-clear after 5 seconds of no further pings from this user
      if (timersRef.current[p.by_user_id]) {
        clearTimeout(timersRef.current[p.by_user_id])
      }
      timersRef.current[p.by_user_id] = setTimeout(() => {
        setActiveTypers(prev => {
          const next = { ...prev }
          delete next[p.by_user_id]
          return next
        })
        delete timersRef.current[p.by_user_id]
      }, 5000)
    }
    window.addEventListener('realtime', handler)
    return () => {
      window.removeEventListener('realtime', handler)
      Object.values(timersRef.current).forEach(t => clearTimeout(t))
      timersRef.current = {}
    }
  }, [matterId, kind, user])

  const names = Object.values(activeTypers).map(t => t.name).filter(Boolean)
  if (names.length === 0) return null
  const label =
    names.length === 1 ? `${names[0]} is typing…` :
    names.length === 2 ? `${names[0]} and ${names[1]} are typing…` :
    `${names[0]} and ${names.length - 1} others are typing…`
  return (
    <div className="text-xs text-primary-700 italic mb-2 flex items-center gap-1" role="status" aria-live="polite">
      <span className="inline-block w-1.5 h-1.5 bg-primary-500 rounded-full animate-pulse" />
      {label}
    </div>
  )
}
